import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import "./Index.css";

// ─── Donut Chart SVG ───
const DonutChart = ({ yesPercent }) => {
  const radius = 80;
  const strokeWidth = 16;
  const center = 100;
  const circumference = 2 * Math.PI * radius;
  const yesStroke = (yesPercent / 100) * circumference;
  const noStroke = circumference - yesStroke;

  // Rotation: start from top (-90deg)
  // Blue dot position at the boundary between yes and no
  const angle = (yesPercent / 100) * 360 - 90;
  const dotX = center + radius * Math.cos((angle * Math.PI) / 180);
  const dotY = center + radius * Math.sin((angle * Math.PI) / 180);

  return (
    <svg width="200" height="200" viewBox="0 0 200 200">
      {/* Background track */}
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#e8e8e8"
        strokeWidth={strokeWidth}
      />
      {/* Yes (like) arc - dark blue */}
      {yesPercent > 0 && (
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#303f9f"
          strokeWidth={strokeWidth}
          strokeDasharray={`${yesStroke} ${noStroke}`}
          strokeDashoffset={circumference * 0.25}
          strokeLinecap="round"
          style={{ transition: "stroke-dasharray 0.6s ease" }}
        />
      )}
      {/* Blue indicator dot */}
      <circle cx={dotX} cy={dotY} r="10" fill="#303f9f" />
    </svg>
  );
};

export default function OpinionPoll({ opinionPolls }) {
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);
  const hasPolls = opinionPolls && opinionPolls.length > 0;
  const [activeIndex, setActiveIndex] = useState(0);

  // Use only one poll object at a time
  const poll = hasPolls ? opinionPolls[activeIndex] : null;

  // Calculate percentages
  const yesCount = poll ? Number(poll.yes || 0) : 0;
  const noCount = poll ? Number(poll.no || 0) : 0;
  const totalVotes = yesCount + noCount;
  const yesPercent =
    totalVotes > 0 ? Math.round((yesCount / totalVotes) * 100) : 0;
  const noPercent = totalVotes > 0 ? 100 - yesPercent : 0;

  return (
    <div className="opinion-wrapper">
      <div className="opinion-card">
        {/* Header */}
        <div className="opinion-header">
          <h2>{t.opinionPoll || "Opinion Poll"}</h2>
        </div>

        <hr className="opinion-divider" />

        {/* Content */}
        <div className="opinion-content">
          {hasPolls && poll ? (
            <div className="opinion-poll-single">
              {/* Poll Question */}
              <h3 className="opinion-poll-question">{poll.name}</h3>

              {/* Donut Chart */}
              <div className="opinion-donut-wrapper">
                <DonutChart yesPercent={yesPercent} />
              </div>

              {/* Votes Polled */}
              <p className="opinion-votes-polled">
                {t.votesPolled || "Votes Polled"} : {totalVotes}
              </p>

              {/* Percentages with thumbs icons */}
              <div className="opinion-results-row">
                <div className="opinion-result-item">
                  <span className="opinion-result-percent">{yesPercent}%</span>
                  <i
                    className="fa fa-thumbs-up opinion-icon-like"
                    aria-hidden="true"
                  ></i>
                </div>
                <div className="opinion-result-divider"></div>
                <div className="opinion-result-item">
                  <span className="opinion-result-percent">{noPercent}%</span>
                  <i
                    className="fa fa-thumbs-down opinion-icon-dislike"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>

              {/* Navigation dots if multiple polls */}
              {opinionPolls.length > 1 && (
                <div className="opinion-dots">
                  {opinionPolls.map((_, i) => (
                    <span
                      key={i}
                      className={`opinion-dot ${i === activeIndex ? "active" : ""}`}
                      onClick={() => setActiveIndex(i)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Bird SVG */}
              <div className="bird-wrapper">
                <svg
                  width="80"
                  height="90"
                  viewBox="0 0 80 90"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <ellipse cx="40" cy="45" rx="28" ry="30" fill="#6B4EAD" />
                  <ellipse cx="40" cy="12" rx="8" ry="12" fill="#6B4EAD" />
                  <circle cx="40" cy="8" r="5" fill="#8B6EC9" />
                  <ellipse cx="32" cy="38" rx="6" ry="7" fill="white" />
                  <ellipse cx="48" cy="38" rx="6" ry="7" fill="white" />
                  <circle cx="33" cy="39" r="3" fill="#333" />
                  <circle cx="49" cy="39" r="3" fill="#333" />
                  <circle cx="34" cy="38" r="1" fill="white" />
                  <circle cx="50" cy="38" r="1" fill="white" />
                  <ellipse cx="40" cy="52" rx="5" ry="4" fill="#FFB74D" />
                  <ellipse
                    cx="26"
                    cy="48"
                    rx="4"
                    ry="2"
                    fill="#E991B0"
                    opacity="0.6"
                  />
                  <ellipse
                    cx="54"
                    cy="48"
                    rx="4"
                    ry="2"
                    fill="#E991B0"
                    opacity="0.6"
                  />
                  <ellipse cx="18" cy="50" rx="10" ry="15" fill="#8B6EC9" />
                  <ellipse cx="62" cy="50" rx="10" ry="15" fill="#8B6EC9" />
                  <path
                    d="M32 75 L28 85 M32 75 L32 85 M32 75 L36 85"
                    stroke="#FF8C00"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <path
                    d="M48 75 L44 85 M48 75 L48 85 M48 75 L52 85"
                    stroke="#FF8C00"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="opinion-text"></p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
